import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonicModule, IonModal, ModalController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { CreateServiceAccountComponent } from 'src/app/components/create-service-account/create-service-account.component';
import {
  FEATURES,
  PROJECT_MEMBER_ROLE_ICONS,
  PROJECT_MEMBER_ROLE_NAMES,
  PROJECT_MEMBER_ROLES,
  ProjectMemberRole,
} from 'src/app/constants';
import { environment } from 'src/environments/environment';
import { ServiceAccount } from 'src/models';
import { ProjectUser } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule, NgxTippyModule],
  selector: 'app-project-service-accounts',
  templateUrl: './project-service-accounts.component.html',
  styleUrls: ['./project-service-accounts.component.css'],
})
export class ProjectServiceAccountsComponent implements OnInit {
  @ViewChild('addServiceAccountModal') addServiceAccountModal!: IonModal;

  public searchText = '';
  public showInactive = false;
  public ColumnMode = ColumnMode;
  public isLoading = true;
  public serviceAccounts: Partial<ProjectUser>[] = [];
  public environment = environment;
  public projectId: string;
  public projectUsers: Partial<ProjectUser>[] = [];
  public projectUser: Partial<ProjectUser>;
  public canEditUsers = false;

  newServiceAccount?: Partial<ServiceAccount>;
  newServiceAccountRole: ProjectMemberRole = PROJECT_MEMBER_ROLES.STANDARD;
  isSubmitting = false;

  featureEnabled = true;

  PROJECT_MEMBER_ROLE_ICONS = PROJECT_MEMBER_ROLE_ICONS;
  PROJECT_MEMBER_ROLE_NAMES = PROJECT_MEMBER_ROLE_NAMES;
  PROJECT_MEMBER_ROLES = PROJECT_MEMBER_ROLES;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
    private route: ActivatedRoute,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    this.projectUser = this.authService.projects.find((p) => p.id === this.projectId);
    this.canEditUsers = this.authService.isTenantOwner || this.projectUser?.role === PROJECT_MEMBER_ROLES.OWNER;

    await this.loadServiceAccounts();

    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.SERVICE_ACCOUNTS);

    this.isLoading = false;
  }

  async loadServiceAccounts() {
    const serviceAccounts = await this.projectsService.listMembers(this.projectId, {
      includeInactive: this.showInactive,
      limit: null,
      order: ['user.name'],
      descending: true,
      where: [
        {
          field: 'entity.isServiceAccount',
          operator: '==',
          value: true,
        },
      ],
    });

    this.serviceAccounts = serviceAccounts.data;
  }

  async createServiceAccount() {
    if (!this.featureEnabled) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: CreateServiceAccountComponent,
      cssClass: 'generate-service-account-modal',
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }) => {
      if (data) {
        this.loadServiceAccounts();

        const confirm = await this.alertCtrl.create({
          header: 'Service Account Key',
          backdropDismiss: false,
          cssClass: 'service-account-token-modal',
          message: `Your new service account has been created with the following key. Save this key and put it somewhere safe. Once this window is closed you will not be able to view it again.<br/><br/><strong>${data}</strong>`,
          buttons: [
            {
              text: 'Copy to clipboard',
              handler: () => {
                navigator.clipboard.writeText(data);

                return false;
              },
            },
            {
              text: 'Close',
            },
          ],
        });

        confirm.present();
      }
    });
  }

  async changeRole(projectUser: ProjectUser) {
    this.searchablePopoverService.projectMemberRoles({
      event,
      includeOwner: this.canEditUsers,
      selectedRole: projectUser.role,
      callback: async (id) => {
        try {
          await this.projectsService.updateMember(this.projectId, projectUser.userId, { role: id });

          this.loadServiceAccounts();
        } catch (error) {
          this.msgSrvc.showError('Failed to change role');
        }
      },
    });
  }

  async removeServiceAccount(userId: string) {
    const alert = await this.alertCtrl.create({
      header: 'Remove user?',
      subHeader: 'This will remove the user from this project.',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Remove',
          handler: async () => {
            try {
              await this.projectsService.removeMember(this.projectId, userId);
              this.msgSrvc.show('User removed from project');
              await this.loadServiceAccounts();
            } catch (error) {
              this.msgSrvc.showError('Failed to remove user');
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async openAddServiceAccountModal() {
    this.newServiceAccount = undefined;
    this.newServiceAccountRole = PROJECT_MEMBER_ROLES.STANDARD;

    await this.addServiceAccountModal.present();
  }

  async showUserOptions() {
    this.searchablePopoverService.serviceAccounts({
      event,
      callback: async (_id: string, _name: string, object: any) => {
        this.newServiceAccount = object;
      },
    });
  }

  async showRoleOptions() {
    this.searchablePopoverService.projectMemberRoles({
      event,
      includeOwner: this.canEditUsers,
      selectedRole: PROJECT_MEMBER_ROLES.STANDARD,
      callback: async (id) => {
        this.newServiceAccountRole = id;
      },
    });
  }

  async submit() {
    this.isSubmitting = true;

    try {
      await this.projectsService.addMember(this.projectId, {
        userId: this.newServiceAccount?.id,
        role: this.newServiceAccountRole,
      });

      this.addServiceAccountModal.dismiss();

      this.msgSrvc.show('Service account added!');

      await this.loadServiceAccounts();
    } catch (error) {
      this.msgSrvc.showError('Failed to add service account');
    } finally {
      this.isSubmitting = false;
    }
  }

  isValidNewServiceAccount(): boolean {
    return Boolean(this.newServiceAccount && this.newServiceAccountRole);
  }
}
